export const FORMAT_KEYS = ['textAlign', 'lineHeight', 'spacingBefore', 'spacingAfter', 'indent', 'textIndent', 'fontSize', 'color', 'backgroundColor', 'padding', 'borderWidth', 'borderStyle', 'borderColor', 'rowHeight', 'tableWidth', 'numbered', 'numberStart', 'numberPrefix', 'hierarchical'];
const bounds = {
    lineHeight: [0.8, 3], spacingBefore: [0, 96], spacingAfter: [0, 96], indent: [0, 288], textIndent: [-96, 96],
    fontSize: [8, 40], padding: [0, 30], borderWidth: [0, 4], rowHeight: [20, 600], tableWidth: [25, 100], numberStart: [1, 10000],
};
export function normalizeFormatting(input, _type) {
    const result = {};
    for (const key of FORMAT_KEYS) {
        const value = input[key];
        if (value === null || value === undefined)
            continue;
        if (key in bounds) {
            const [min, max] = bounds[key];
            if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max || (key === 'numberStart' && !Number.isInteger(value)))
                throw new Error(`Invalid ${key}`);
        }
        else if (key === 'textAlign') {
            if (typeof value !== 'string' || !['left', 'center', 'right', 'justify'].includes(value))
                throw new Error('Invalid textAlign');
        }
        else if (key === 'borderStyle') {
            if (typeof value !== 'string' || !['solid', 'dashed', 'dotted', 'none'].includes(value))
                throw new Error('Invalid borderStyle');
        }
        else if (['color', 'backgroundColor', 'borderColor'].includes(key)) {
            if (typeof value !== 'string' || !/^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i.test(value))
                throw new Error(`Invalid ${key}`);
        }
        else if (key === 'numberPrefix') {
            if (typeof value !== 'string' || value.length > 30 || (value !== '' && !/^\d{1,4}(?:\.\d{1,4})*$/.test(value)))
                throw new Error('Invalid numberPrefix');
        }
        else if (typeof value !== 'boolean')
            throw new Error(`Invalid ${key}`);
        result[key] = value;
    }
    return result;
}
export function blockStyle(attrs) {
    const a = normalizeFormatting(attrs);
    const properties = { textAlign: 'text-align', lineHeight: 'line-height', spacingBefore: 'margin-top', spacingAfter: 'margin-bottom', indent: 'margin-left', textIndent: 'text-indent', fontSize: 'font-size', color: 'color' };
    return Object.entries(properties).filter(([key]) => a[key] !== undefined).map(([key, property]) => `${property}:${a[key]}${['spacingBefore', 'spacingAfter', 'indent', 'textIndent', 'fontSize'].includes(key) ? 'px' : ''}`).join(';');
}
export function cellStyle(attrs) {
    const a = normalizeFormatting(attrs);
    return [blockStyle(attrs), ...Object.entries({ backgroundColor: 'background-color', padding: 'padding', borderWidth: 'border-width', borderStyle: 'border-style', borderColor: 'border-color' }).filter(([key]) => a[key] !== undefined).map(([key, property]) => `${property}:${a[key]}${['padding', 'borderWidth'].includes(key) ? 'px' : ''}`)].filter(Boolean).join(';');
}
export function paperGeometry(format) { return format === 'a4' ? { width: 794, height: 1123, margin: 48 } : { width: 816, height: 1056, margin: 48 }; }
/** Fit displayed columns including collapsed borders; never mutate signed widths. */
export function tableDisplayGeometry(table, availableWidth = 720) {
    const formatting = normalizeFormatting(table.attrs || {});
    const width = Math.max(1, availableWidth) * Number(formatting.tableWidth ?? 100) / 100;
    const rows = table.content || [];
    const border = (cell) => {
        const attrs = normalizeFormatting(cell?.attrs || {});
        return attrs.borderStyle === 'none' ? 0 : Number(attrs.borderWidth ?? 1.333);
    };
    const left = Math.max(0, ...rows.map(row => border(row.content?.[0])));
    const right = Math.max(0, ...rows.map(row => border(row.content?.[row.content.length - 1])));
    const borderBudget = (left + right) / 2;
    const weights = (rows[0]?.content || []).flatMap(cell => {
        const values = Array.isArray(cell.attrs?.colwidth) ? cell.attrs.colwidth : [];
        const span = Math.max(1, Number(cell.attrs?.colspan) || 1);
        return Array.from({ length: span }, (_, index) => {
            const value = values[index];
            return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
        });
    });
    const known = weights.filter(value => value > 0);
    const fallback = known.length ? known.reduce((sum, value) => sum + value, 0) / known.length : 1;
    const completed = weights.map(value => value || fallback);
    const total = completed.reduce((sum, value) => sum + value, 0);
    return { width, colwidth: completed.map(value => value * Math.max(1, width - borderBudget) / total), borderBudget };
}
/** Chromium places its print header at 20 CSS pixels from the physical top. */
export const PROCEDURE_HEADER_TOP = 20;
/**
 * Total physical body offset, in CSS px. This preserves the existing PDF
 * reservation exactly; screen page headers use the same fixed reserve rather
 * than their independently measured DOM height. No source JSON is modified.
 */
export function procedureHeaderReservation(metadata, language, modern = true) {
    const effectiveDate = metadata.effectiveDate || 'YYYY-MM-DD';
    const revision = metadata.revision || '0';
    const values = language === 'en'
        ? [`Title: ${metadata.title || '[English title]'}`, `Document No : ${metadata.controlCode || 'XX.XX.XX'}`, `Document Owner : ${metadata.department || '[English department]'}`, `Effective Date: ${effectiveDate}`, `Revision No: ${revision}`]
        : [`Titre: ${metadata.title || '[Titre français]'}`, `N⁰ de Document: ${metadata.controlCode || 'XX.XX.XX'}`, `Propriétaire du document: ${metadata.department || '[Département français]'}`, `Date mise en vigueur: ${effectiveDate}`, `N⁰ de Révision: ${revision}`];
    const widths = language === 'en' ? [225, 120, 104.25] : [194.25, 150.75, 104.25];
    if (modern) {
        const proportions = language === 'en' ? [4500, 2400, 2085] : [3885, 3015, 2085];
        proportions.forEach((width, index) => { widths[index] = 447 * width / 8985; });
    }
    const lines = (value, width) => Math.max(1, Math.ceil(value.length * 8 / (width - 10)));
    const firstRow = Math.max(lines(values[0], widths[0]), lines(values[1], widths[1]), 2);
    const secondRow = Math.max(lines(values[2], widths[0]), lines(values[3], widths[1]), lines(values[4], widths[2]));
    return Number(Math.max(1.08, ((firstRow + secondRow) * 9.6 + 22) / 72 + 0.25).toFixed(3)) * 96;
}
const textOf = (n) => n.text || (n.content || []).map(textOf).join('');
export function headingNumbers(root) {
    const output = new Map();
    let counters = [0, 0, 0];
    const walk = (node) => {
        if (node.type === 'procedureSection')
            counters = [0, 0, 0];
        if (node.type === 'heading') {
            const level = Math.max(1, Math.min(3, Number(node.attrs?.level) || 1));
            const manual = /^(\d{1,4}(?:\.\d{1,4}){0,2})[.\s]/.exec(textOf(node));
            if (manual && !node.attrs?.numbered) {
                const parts = manual[1].split('.').map(Number);
                parts.forEach((v, i) => { counters[i] = v; });
                for (let i = parts.length; i < 3; i++)
                    counters[i] = 0;
            }
            else if (node.attrs?.numbered) {
                const a = normalizeFormatting(node.attrs);
                if (typeof a.numberPrefix === 'string' && a.numberPrefix) {
                    const prefix = a.numberPrefix.split('.').map(Number);
                    prefix.forEach((v, i) => { if (i < level - 1)
                        counters[i] = v; });
                }
                for (let i = 0; i < level - 1; i++)
                    if (!counters[i])
                        counters[i] = 1;
                counters[level - 1] = typeof a.numberStart === 'number' ? a.numberStart : counters[level - 1] + 1;
                for (let i = level; i < 3; i++)
                    counters[i] = 0;
                const label = typeof a.numberPrefix === 'string' && a.numberPrefix ? `${a.numberPrefix}.${counters[level - 1]}` : counters.slice(0, level).join('.');
                output.set(node, label);
            }
        }
        node.content?.forEach(walk);
    };
    walk(root);
    return output;
}
export const DOCUMENT_CSS = `
.eqms-authoring-content{font:13.333px/1.15 Arial,Helvetica,sans-serif;color:#000;overflow-wrap:anywhere;text-align:left}
.eqms-authoring-content h1,.eqms-authoring-content h2,.eqms-authoring-content h3{font-family:Arial,Helvetica,sans-serif;break-after:avoid;break-inside:avoid;line-height:1.2}
.eqms-authoring-content h1{font-size:18.667px;font-weight:700;margin:26.667px 0 13.333px}
.eqms-authoring-content h2{font-size:16px;font-weight:700;margin:13.333px 0 8px}
.eqms-authoring-content h3{font-size:14.667px;font-weight:700;margin:10px 0 6px}
.eqms-authoring-content p{font-size:13.333px;margin:0 0 8px;min-height:1em;orphans:2;widows:2}
.eqms-authoring-content ul,.eqms-authoring-content ol{margin:0 0 8px;padding-left:32px}
.eqms-authoring-content li>p{margin:0}.eqms-authoring-content ul{list-style-type:disc}
.eqms-authoring-content ol{list-style-type:decimal}.eqms-authoring-content ol[data-hierarchical="true"]{list-style:none;counter-reset:eqms-item;padding-left:0}
.eqms-authoring-content ol[data-hierarchical="true"]>li{counter-increment:eqms-item;position:relative;padding-left:var(--eqms-marker-width,48px)}
.eqms-authoring-content ol[data-hierarchical="true"]>li:before{content:var(--eqms-prefix,"") counters(eqms-item,".") ". ";position:absolute;left:0;top:0;font-variant-numeric:tabular-nums}
.eqms-authoring-content .eqms-heading-number{margin-right:.35em;font-variant-numeric:tabular-nums;user-select:none}
.eqms-authoring-content table{width:100%;border-collapse:collapse;table-layout:fixed;margin:0 0 8px}
.eqms-authoring-content td,.eqms-authoring-content th{border:1.333px solid #000;padding:6.667px;vertical-align:top;text-align:left;font-size:12px}
.eqms-authoring-content th{font-weight:700;background:#fff}.eqms-authoring-content td p,.eqms-authoring-content th p{margin:0;font-size:inherit;line-height:inherit}
.eqms-authoring-content tr{break-inside:avoid}.eqms-authoring-content figure{break-inside:avoid;margin:13px 0}
.eqms-authoring-content img{display:block;max-width:100%;max-height:720px;object-fit:contain}
.eqms-authoring-content blockquote{margin:8px 0;padding-left:13px;border-left:1.333px solid #777}
.eqms-authoring-content pre{white-space:pre-wrap;padding:10px;background:#f3f3f3}.eqms-authoring-content code{font:12px monospace}
.eqms-authoring-content a{color:#000;text-decoration:underline}.eqms-authoring-content hr{border:0;border-top:1.333px solid #000;margin:13px 0}
.eqms-authoring-content .selectedCell{outline:2px solid #0f766e;outline-offset:-2px}.eqms-authoring-content .column-resize-handle{position:absolute;right:-2px;top:0;bottom:0;width:4px;background:#0f766e;pointer-events:none}
.eqms-authoring-content td,.eqms-authoring-content th{position:relative}.eqms-authoring-content.resize-cursor{cursor:col-resize}
`;
//# sourceMappingURL=index.js.map